import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { getTrackerById, getTrackers } from "../trackerStorage";
import { getTrackersApi } from "../../../DAL/tracker/tracker";
import {
  createEmptyAutomation,
  deleteWorkflowFromAutomationViaApi,
  getActionLabel,
  loadAutomationByIdFromApi,
  saveAutomationViaApi,
} from "./automationStorage";
import WorkflowEditorPanel from "./WorkflowEditorPanel";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

/**
 * Create + edit share the same layout:
 * top meta (name / tracker / enable) + left workflows + right editor.
 */
export default function AddOrEditAutomationPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id || id === "new";

  const [form, setForm] = useState(() =>
    createEmptyAutomation(
      searchParams.get("trackerId")
        ? getTrackerById(searchParams.get("trackerId"))
        : null
    )
  );
  const [errors, setErrors] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteWorkflow, setDeleteWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [editMetaOpen, setEditMetaOpen] = useState(false);
  const [metaDraft, setMetaDraft] = useState({
    name: "",
    enabled: true,
  });
  const [metaErrors, setMetaErrors] = useState({});
  const [trackerOptions, setTrackerOptions] = useState(() =>
    getTrackers().filter((t) => t?.id)
  );

  const selectedWorkflowParam = searchParams.get("workflow") || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTrackersApi({ limit: 200 });
        if (cancelled) return;
        if (res?.code === 200 && Array.isArray(res.trackers)) {
          setTrackerOptions(
            res.trackers.map((t) => ({
              ...t,
              id: String(t._id || t.id),
            }))
          );
        }
      } catch (_e) {
        /* keep local options */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (isNew) {
        const trackerId = searchParams.get("trackerId");
        const tracker = trackerId
          ? getTrackerById(trackerId) ||
            trackerOptions.find((t) => t.id === trackerId)
          : null;
        setForm(createEmptyAutomation(tracker));
        setNotFound(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      const res = await loadAutomationByIdFromApi(id);
      if (cancelled) return;
      if (res?.automation) {
        setForm(res.automation);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, searchParams, trackerOptions]);

  const workflows = form.workflows || [];

  const selectedIsNew = selectedWorkflowParam === "new";
  const selectedWorkflowId =
    selectedIsNew || !selectedWorkflowParam ? null : selectedWorkflowParam;

  // Auto-select first workflow when none selected (edit only)
  useEffect(() => {
    if (isNew || !form?.id) return;
    if (selectedWorkflowParam) return;
    if (!workflows.length) return;
    const next = new URLSearchParams(searchParams);
    next.set("workflow", workflows[0].id);
    setSearchParams(next, { replace: true });
  }, [
    isNew,
    form?.id,
    workflows,
    selectedWorkflowParam,
    searchParams,
    setSearchParams,
  ]);

  const setSelectedWorkflow = (workflowKey) => {
    const next = new URLSearchParams(searchParams);
    if (!workflowKey) next.delete("workflow");
    else next.set("workflow", workflowKey);
    setSearchParams(next, { replace: true });
  };

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (patch.trackerId && patch.trackerId !== prev.trackerId) {
        const tracker =
          getTrackerById(patch.trackerId) ||
          trackerOptions.find((t) => t.id === patch.trackerId);
        next.trackerName = tracker?.name || "";
        if (!prev.name || prev.name === `${prev.trackerName} Automation`) {
          next.name = tracker?.name
            ? `${tracker.name} Automation`
            : prev.name;
        }
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => {
        next[key] = "";
      });
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = "Name is required";
    if (!form.trackerId) next.trackerId = "Pick a tracker";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const persistAutomation = async () => {
    if (!validate()) return null;
    try {
      const saved = await saveAutomationViaApi({
        ...form,
        name: form.name.trim(),
        description: (form.description || "").trim(),
        enabled: form.enabled !== false,
        status: form.enabled !== false ? "active" : "draft",
      });
      return saved;
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save automation", {
        variant: "error",
      });
      return null;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await persistAutomation();
    setSaving(false);
    if (!saved) return;
    enqueueSnackbar(isNew ? "Automation created" : "Automation saved", {
      variant: "success",
    });
    if (isNew && saved?.id) {
      navigate(`/tracker-automations/${saved.id}`, { replace: true });
    } else {
      setForm(saved);
      setEditMetaOpen(false);
    }
  };

  const openEditMeta = () => {
    setMetaDraft({
      name: form.name || "",
      enabled: form.enabled !== false,
    });
    setMetaErrors({});
    setEditMetaOpen(true);
  };

  const handleSaveMeta = async () => {
    const nextErrors = {};
    if (!String(metaDraft.name || "").trim()) {
      nextErrors.name = "Name is required";
    }
    setMetaErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const saved = await saveAutomationViaApi({
        ...form,
        name: String(metaDraft.name || "").trim(),
        description: (form.description || "").trim(),
        enabled: metaDraft.enabled !== false,
        status: metaDraft.enabled !== false ? "active" : "draft",
      });
      setForm(saved);
      setEditMetaOpen(false);
      enqueueSnackbar("Automation saved", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save automation", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /** Ensure parent exists before opening workflow editor (create flow). */
  const ensureSavedThen = async (workflowKey) => {
    if (form?.id) {
      setSelectedWorkflow(workflowKey);
      return;
    }
    setSaving(true);
    const saved = await persistAutomation();
    setSaving(false);
    if (!saved?.id) return;
    enqueueSnackbar("Automation created", { variant: "success" });
    const qs =
      workflowKey != null
        ? `?workflow=${encodeURIComponent(workflowKey)}`
        : "";
    navigate(`/tracker-automations/${saved.id}${qs}`, { replace: true });
  };

  const handleDeleteWorkflow = async () => {
    if (!deleteWorkflow?.id || !form?.id) return;
    try {
      const saved = await deleteWorkflowFromAutomationViaApi(
        form.id,
        deleteWorkflow.id
      );
      setForm(saved || form);
      setOpenDelete(false);
      const wasSelected = selectedWorkflowId === deleteWorkflow.id;
      setDeleteWorkflow(null);
      enqueueSnackbar("Workflow deleted", { variant: "success" });
      if (wasSelected) {
        const remaining = (saved?.workflows || []).filter(
          (w) => w.id !== deleteWorkflow.id
        );
        setSelectedWorkflow(remaining[0]?.id || null);
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not delete workflow", {
        variant: "error",
      });
    }
  };

  const handleAddWorkflow = () => {
    ensureSavedThen("new");
  };

  if (loading) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box sx={{ py: 4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
            Automation not found
          </Typography>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => navigate("/tracker-automations")}
          >
            Back to Automations
          </button>
        </Box>
      </div>
    );
  }

  const showEditor = Boolean(form.id) && (selectedIsNew || selectedWorkflowId);
  const title = isNew ? "Create automation" : form.name || "Automation";

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this workflow?"
        handleAgree={handleDeleteWorkflow}
      />

      <Dialog
        open={editMetaOpen}
        onClose={() => !saving && setEditMetaOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>
          Edit automation
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            size="small"
            label="Name"
            required
            fullWidth
            value={metaDraft.name}
            onChange={(e) =>
              setMetaDraft((prev) => ({ ...prev, name: e.target.value }))
            }
            error={Boolean(metaErrors.name)}
            helperText={metaErrors.name || " "}
            sx={{ ...fieldSx, mb: 1 }}
            autoFocus
          />
          <TextField
            size="small"
            label="Tracker"
            fullWidth
            value={form.trackerName || "—"}
            disabled
            sx={{ ...fieldSx, mb: 1.5 }}
            helperText=" "
          />
          <FormControlLabel
            sx={{ ml: 0.25 }}
            control={
              <Switch
                checked={metaDraft.enabled !== false}
                onChange={(e) =>
                  setMetaDraft((prev) => ({
                    ...prev,
                    enabled: e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Enabled
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => setEditMetaOpen(false)}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="small-contained-button"
            onClick={handleSaveMeta}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogActions>
      </Dialog>

      <div className="container-fluid tracker-module-page">
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <IconButton
                size="small"
                onClick={() => navigate("/tracker-automations")}
                sx={{ p: 0.75 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography
                component="button"
                type="button"
                onClick={() => navigate("/tracker-automations")}
                sx={{
                  border: 0,
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  color: MUTED,
                  fontSize: 13,
                  lineHeight: 1.2,
                  "&:hover": { color: PRIMARY },
                }}
              >
                All automations
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13, lineHeight: 1.2 }}>
                /
              </Typography>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  maxWidth: 320,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 22, md: 26 },
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {form.trackerName ? (
              <Typography sx={{ mt: 0.6, fontSize: 13, color: MUTED }}>
                Tracker · {form.trackerName}
                {form.enabled === false ? " · Disabled" : " · Enabled"}
              </Typography>
            ) : (
              <Typography sx={{ mt: 0.6, fontSize: 13, color: MUTED }}>
                Set name & tracker, then add workflows below.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {!isNew ? (
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={openEditMeta}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                Edit
              </button>
            ) : (
              <button
                type="button"
                className="small-contained-button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Creating…" : "Create automation"}
              </button>
            )}
          </Box>
        </Box>

        {/* Create only — name / tracker fields on the page */}
        {isNew ? (
          <Box
            sx={{
              mb: 2,
              p: { xs: 2, md: 2.25 },
              border: `1px solid ${BORDER}`,
              borderRadius: "12px",
              bgcolor: "#fff",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr auto",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <TextField
              size="small"
              label="Name"
              required
              fullWidth
              value={form.name || ""}
              onChange={(e) => update({ name: e.target.value })}
              error={Boolean(errors.name)}
              helperText={errors.name || " "}
              sx={fieldSx}
            />
            <TextField
              select
              size="small"
              label="Tracker"
              required
              fullWidth
              value={form.trackerId || ""}
              onChange={(e) => update({ trackerId: e.target.value })}
              error={Boolean(errors.trackerId)}
              helperText={errors.trackerId || " "}
              sx={fieldSx}
            >
              <MenuItem value="">Select tracker…</MenuItem>
              {trackerOptions.map((tracker) => (
                <MenuItem key={tracker.id} value={tracker.id}>
                  {tracker.name || "Untitled"}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              sx={{ mt: { xs: 0, md: 0.5 }, ml: 0.5 }}
              control={
                <Switch
                  checked={form.enabled !== false}
                  onChange={(e) => update({ enabled: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Enabled
                </Typography>
              }
            />
          </Box>
        ) : null}

        {/* Workflows — primary content on detail */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            height: {
              xs: "auto",
              md: isNew ? "calc(100vh - 320px)" : "calc(100vh - 220px)",
            },
            minHeight: { md: 420 },
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#fff",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 280 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              borderRight: { md: `1px solid ${BORDER}` },
              borderBottom: { xs: `1px solid ${BORDER}`, md: 0 },
              maxHeight: { xs: 260, md: "none" },
              height: { md: "100%" },
              bgcolor: "#fafbfc",
            }}
          >
            <Box
              sx={{
                px: 1.75,
                py: 1.35,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                borderBottom: `1px solid ${BORDER}`,
                bgcolor: "#fff",
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: 13.5, color: "#0f172a" }}
              >
                Workflows
              </Typography>
              <button
                type="button"
                className="small-contained-button"
                onClick={handleAddWorkflow}
                disabled={saving}
              >
                Add
              </button>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {workflows.length === 0 && !selectedIsNew ? (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: MUTED,
                    p: 2.5,
                    textAlign: "center",
                  }}
                >
                  No workflows yet.
                </Typography>
              ) : (
                <>
                  {selectedIsNew ? (
                    <Box
                      sx={{
                        px: 1.75,
                        py: 1.25,
                        bgcolor: PRIMARY_SOFT,
                        borderLeft: `3px solid ${PRIMARY}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: PRIMARY,
                        }}
                      >
                        New workflow
                      </Typography>
                    </Box>
                  ) : null}

                  {workflows.map((item) => {
                    const active =
                      !selectedIsNew && selectedWorkflowId === item.id;
                    return (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          alignItems: "stretch",
                          borderBottom: `1px solid #f1f5f9`,
                          bgcolor: active ? PRIMARY_SOFT : "transparent",
                          borderLeft: active
                            ? `3px solid ${PRIMARY}`
                            : "3px solid transparent",
                          "&:hover": {
                            bgcolor: active ? PRIMARY_SOFT : "#fff",
                          },
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSelectedWorkflow(item.id)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            textAlign: "left",
                            border: 0,
                            background: "none",
                            cursor: "pointer",
                            px: 1.75,
                            py: 1.2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 13.5,
                              color: active ? PRIMARY : "#0f172a",
                              mb: 0.2,
                            }}
                          >
                            {item.name || "Untitled"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              color: MUTED,
                              lineHeight: 1.35,
                            }}
                          >
                            {item.status === "active" ? "Live" : "Draft"} ·{" "}
                            {getActionLabel(item.action)}
                          </Typography>
                        </Box>
                        {form.id ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteWorkflow(item);
                              setOpenDelete(true);
                            }}
                            sx={{
                              alignSelf: "center",
                              mr: 0.75,
                              flexShrink: 0,
                              color: MUTED,
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : null}
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 360, md: 0 },
              height: { md: "100%" },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
            }}
          >
            {showEditor ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <WorkflowEditorPanel
                  key={selectedIsNew ? "new" : selectedWorkflowId}
                  automation={form}
                  workflowId={selectedWorkflowId}
                  isNew={selectedIsNew}
                  onCancel={
                    selectedIsNew
                      ? () => setSelectedWorkflow(workflows[0]?.id || null)
                      : undefined
                  }
                  onSaved={(saved, workflow) => {
                    setForm(saved);
                    if (workflow?.id) {
                      setSelectedWorkflow(workflow.id);
                    }
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                }}
              >
                <Box sx={{ textAlign: "center", maxWidth: 360 }}>
                  <Typography sx={{ fontSize: 14, color: MUTED, mb: 1.5 }}>
                    {isNew
                      ? "Pick name & tracker above, then add a workflow."
                      : "Select a workflow or add a new one."}
                  </Typography>
                  <button
                    type="button"
                    className="small-contained-button"
                    onClick={handleAddWorkflow}
                    disabled={saving}
                  >
                    Add workflow
                  </button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </div>
    </>
  );
}
